import { type KeyboardEvent as ReactKeyboardEvent, useEffect, useRef } from 'react'

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function useDialogFocus(active: boolean, onClose: () => void) {
  const dialogRef = useRef<HTMLElement | null>(null)
  const previousFocus = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!active) return
    previousFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const frame = requestAnimationFrame(() => {
      const first = dialogRef.current?.querySelector<HTMLElement>('[data-autofocus], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [href]')
      ;(first || dialogRef.current)?.focus()
    })
    return () => {
      cancelAnimationFrame(frame)
      previousFocus.current?.focus()
    }
  }, [active])

  const onKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
      return
    }
    if (event.key !== 'Tab' || !dialogRef.current) return
    const elements = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(element => !element.hasAttribute('disabled') && element.offsetParent !== null)
    if (elements.length === 0) {
      event.preventDefault()
      dialogRef.current.focus()
      return
    }
    const first = elements[0]
    const last = elements[elements.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  return { dialogRef, onKeyDown }
}
